import * as arrow from "apache-arrow";
import React, { useEffect, useRef, useState } from "react";

import { DatePivotParams } from "../../entities/date_pivot_params";
import { PivotViewConfig } from "../../entities/pivot_view_config";
import {
  getFlatTableData,
  getChunkedFlatTableData,
  getPivotTableData,
  getComparisonTableData,
} from "../../utils/mui_flat_table_data";
import { isChunkedParsedData } from "../../utils/parquet";
import {
  CHUNKED_MATRIX_TRANSFORM_IDS,
  useChunkedMatrixTableData,
} from "../../utils/chunked_inventory_health_matrix";
import type { FullParsedParquetData } from "../../utils/parquet";
import { MUIFlatTable } from "./MUIFlatTable";
import { useTheme } from "@mui/material/styles";
import { Box } from "@mui/material";

export type TableType = "pivot" | "flat";

export interface UniversalTableViewerProps<
  T extends DatePivotParams = DatePivotParams,
> {
  parsedData:
    | { arrowTable: arrow.Table; columns: arrow.Field<any>[] }
    | import("../../utils/parquet").ChunkedParsedParquetData
    | null;
  viewParams: T;
  viewControllerConfig?: PivotViewConfig<T>;
  columnHeaderMap?: Record<string, string>;
  columnFormatters?: Record<string, (value: any, row: any) => React.ReactNode>;
  tableType: TableType | undefined;
  loading: boolean;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  expandedGroups?: Set<string>;
  setExpandedGroups?: React.Dispatch<React.SetStateAction<Set<string>>>;
  fetchSortKeys?: (measure: string) => Promise<FullParsedParquetData | null>;
}

function ChunkedMatrixTableWrapper<T extends DatePivotParams = DatePivotParams>(
  props: {
    parsedData: import("../../utils/parquet").ChunkedParsedParquetData;
    viewParams: T;
    viewControllerConfig: PivotViewConfig<T>;
    sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
    setSortConfig: React.Dispatch<
      React.SetStateAction<
        Array<{ key: string; direction: "asc" | "desc"; index: number }>
      >
    >;
    expandedGroups?: Set<string>;
    setExpandedGroups?: React.Dispatch<React.SetStateAction<Set<string>>>;
    columnHeaderMap?: Record<string, string>;
    shadeHistory?: boolean;
    showHeaderRow?: boolean;
    fetchSortKeys?: (measure: string) => Promise<FullParsedParquetData | null>;
  }
) {
  const theme = useTheme();
  const {
    parsedData,
    viewParams,
    sortConfig,
    setSortConfig,
    expandedGroups = new Set(),
    setExpandedGroups = () => {},
    columnHeaderMap,
    shadeHistory,
    showHeaderRow,
    fetchSortKeys,
  } = props;
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const shouldExpandHeader = isHeaderExpanded || expandedGroups.has("*");

  const tableData = useChunkedMatrixTableData({
    chunkedData: parsedData,
    viewParams,
    expandedGroups,
    setExpandedGroups,
    theme,
    sortConfig,
    setSortConfig,
    fetchSortKeys,
  });

  if (!tableData.isReady) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
        Loading table…
      </Box>
    );
  }

  return (
    <MUIFlatTable
      columns={tableData.columns}
      getRow={tableData.getRow}
      rowCount={tableData.rowCount}
      columnHeaderMap={columnHeaderMap}
      sortConfig={sortConfig}
      columnTotals={tableData.columnTotals}
      expandedGroups={expandedGroups}
      onToggleGroup={
        setExpandedGroups
          ? (groupKey: string) => {
              setExpandedGroups((prev) => {
                const next = new Set(prev);
                if (next.has(groupKey)) next.delete(groupKey);
                else next.add(groupKey);
                return next;
              });
            }
          : undefined
      }
      isHeaderExpanded={shouldExpandHeader}
      onToggleHeader={() => setIsHeaderExpanded(!isHeaderExpanded)}
      shadeHistory={shadeHistory}
      showHeaderRow={showHeaderRow}
    />
  );
}

export const UniversalTableViewer = <
  T extends DatePivotParams = DatePivotParams,
>(
  props: UniversalTableViewerProps<T>
) => {
  const {
    parsedData,
    viewParams,
    viewControllerConfig,
    columnHeaderMap,
    columnFormatters,
    tableType,
    loading,
    sortConfig,
    setSortConfig,
    columnSchema,
    expandedGroups,
    setExpandedGroups,
    fetchSortKeys,
  } = props;

  const theme = useTheme();
  const firstMount = useRef(true);

  // Header expansion state for comparison tables
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  // Check if wildcard expansion is requested
  const shouldExpandHeader = isHeaderExpanded || expandedGroups?.has("*");

  useEffect(() => {
    if (parsedData && firstMount.current) {
      firstMount.current = false;
    }
  }, [parsedData]);

  // Sorting logic: clicking a column toggles its sort direction, single column sort by default, multi-column sort with shift key
  const handleSort = (
    key: string,
    e?: React.MouseEvent<HTMLTableCellElement>
  ) => {
    setSortConfig((prev) => {
      const isShiftPressed = e?.shiftKey || false;
      const existingIndex = prev.findIndex((s) => s.key === key);
      const existingSort = existingIndex >= 0 ? prev[existingIndex] : null;

      // If shift not pressed, start with empty array (single column sort)
      const baseConfig = isShiftPressed ? [...prev] : [];

      if (existingSort) {
        // Column is already sorted - toggle direction or remove
        const newDirection = existingSort.direction === "asc" ? "desc" : null;

        if (newDirection) {
          const index = isShiftPressed ? existingIndex : 0;
          baseConfig[index] = {
            key,
            direction: newDirection,
            index,
          };
          return baseConfig;
        }

        return baseConfig.filter((_, idx) => idx !== existingIndex);
      }

      // New column - add as ascending
      return [
        ...baseConfig,
        { key, direction: "asc", index: baseConfig.length },
      ];
    });
  };

  if (loading || !parsedData) {
    return null;
  }

  // Chunked matrix path: matrix-shaped parquet (supply_walk_parallelized_matrix, etc.)
  const effectiveTransformId =
    (viewControllerConfig as { chunkedMatrixTransformId?: string })
      ?.chunkedMatrixTransformId ?? viewControllerConfig?.transformId;
  if (
    isChunkedParsedData(parsedData) &&
    viewControllerConfig &&
    effectiveTransformId &&
    CHUNKED_MATRIX_TRANSFORM_IDS.has(effectiveTransformId)
  ) {
    return (
      <ChunkedMatrixTableWrapper
        parsedData={parsedData}
        viewParams={viewParams}
        viewControllerConfig={viewControllerConfig}
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
        expandedGroups={expandedGroups}
        setExpandedGroups={setExpandedGroups}
        columnHeaderMap={columnHeaderMap}
        shadeHistory={viewControllerConfig.shadeHistory}
        showHeaderRow={viewControllerConfig.showHeaderRow}
        fetchSortKeys={fetchSortKeys}
      />
    );
  }

  // Use the new virtualization-friendly data functions
  let tableData;

  // Chunked path: flat table only, no full arrowTable
  if (isChunkedParsedData(parsedData)) {
    tableData = getChunkedFlatTableData({
      rowCount: parsedData.rowCount,
      getRow: parsedData.getRow,
      columns: parsedData.columns,
      sortConfig,
      onSort: handleSort,
      columnSchema,
      setSortConfig,
    });
  } else if (viewControllerConfig?.getMatrix) {
    // Use custom getMatrix function if provided, otherwise fall back to default logic
    tableData = viewControllerConfig.getMatrix({
      arrowTable: parsedData.arrowTable,
      columns: parsedData.columns,
      viewParams,
      demandWalkConfig: viewControllerConfig.demandWalkConfig,
      purchaseOrderConfig: viewControllerConfig.purchaseOrderConfig,
      sortConfig,
      onSort: handleSort,
      setSortConfig,
      columnSchema: columnSchema || {},
      theme,
      expandedGroups: expandedGroups || new Set(),
      setExpandedGroups: setExpandedGroups || (() => {}),
      columnHeaderMap,
      firstMount,
    });
  } else {
    if (parsedData.columns.some((col) => col.name === "table_id")) {
      tableData = getComparisonTableData({
        arrowTable: parsedData.arrowTable,
        columns: parsedData.columns,
        viewParams,
        sortConfig,
        onSort: handleSort,
        columnSchema,
        setSortConfig,
        firstMount,
        expandedGroups: expandedGroups || new Set(),
        setExpandedGroups: setExpandedGroups || (() => {}),
      });
    } else if (tableType === "flat") {
      tableData = getFlatTableData({
        arrowTable: parsedData.arrowTable,
        columns: parsedData.columns,
        sortConfig,
        onSort: handleSort,
        columnSchema,
        setSortConfig,
        firstMount,
      });
    } else {
      tableData = getPivotTableData({
        arrowTable: parsedData.arrowTable,
        columns: parsedData.columns,
        viewParams,
        sortConfig,
        onSort: handleSort,
        columnSchema,
        setSortConfig,
        firstMount,
      });
    }
  }

  if (!tableData) {
    return null;
  }

  const { columns, getRow, rowCount, columnTotals } = tableData;

  return (
    <MUIFlatTable
      columns={columns}
      getRow={getRow}
      rowCount={rowCount}
      columnHeaderMap={columnHeaderMap}
      columnFormatters={columnFormatters}
      sortConfig={sortConfig}
      shadeHistory={viewControllerConfig?.shadeHistory}
      showHeaderRow={viewControllerConfig?.showHeaderRow}
      columnTotals={columnTotals}
      expandedGroups={expandedGroups}
      onToggleGroup={
        setExpandedGroups
          ? (groupKey: string) => {
              setExpandedGroups((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(groupKey)) {
                  newSet.delete(groupKey);
                } else {
                  newSet.add(groupKey);
                }
                return newSet;
              });
            }
          : undefined
      }
      isHeaderExpanded={shouldExpandHeader}
      onToggleHeader={() => setIsHeaderExpanded(!isHeaderExpanded)}
    />
  );
};

export { MUIFlatTable };
